import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactFlow, { Background, BackgroundVariant, Controls, MiniMap, type Connection, type EdgeChange, type IsValidConnection, type NodeChange, type ReactFlowInstance } from 'reactflow';
import { AlignHorizontalSpaceAround, Copy, FileOutput, FilePlus2, Maximize2, Plus, Trash2 } from 'lucide-react';
import 'reactflow/dist/style.css';
import { FILTER_PRESETS, type FilterPreset } from '../data/filterCatalog';
import type { NodeData, WorkflowDocument, WorkflowEdge, WorkflowNode } from '../nodes/NodeTypes';
import InputNode from '../nodes/InputNode';
import ProcessingNode from '../nodes/ProcessingNode';
import OutputNode from '../nodes/OutputNode';
import FilterLibrary from './FilterLibrary';
import NodeInspector from './NodeInspector';

const nodeTypes = { input: InputNode, processing: ProcessingNode, output: OutputNode };

interface AdvancedWorkspaceProps {
  workflow: WorkflowDocument;
  selectedNode?: WorkflowNode;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onPresetDragStart: (event: React.DragEvent<HTMLDivElement>, preset: FilterPreset) => void;
  onAddPreset: (preset: FilterPreset) => void;
  onAddInput: () => void;
  onAddOutput: () => void;
  onAddPresetAt: (preset: FilterPreset, position: { x: number; y: number }) => void;
  onAddInputAt: (position: { x: number; y: number }) => void;
  onAddOutputAt: (position: { x: number; y: number }) => void;
  onDuplicateNode: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  onUpdateNode: (nodeId: string, patch: Partial<NodeData>) => void;
  onDeleteNode: (nodeId: string) => void;
  onNodeClick: (_event: React.MouseEvent, node: WorkflowNode) => void;
  onInit: (instance: ReactFlowInstance) => void;
  onFitView: () => void;
  onAutoLayout: () => void;
  isValidConnection: IsValidConnection;
}

const AdvancedWorkspace: React.FC<AdvancedWorkspaceProps> = ({
  workflow, selectedNode, onNodesChange, onEdgesChange, onConnect, onDrop, onDragOver, onPresetDragStart,
  onAddPreset, onAddInput, onAddOutput, onAddPresetAt, onAddInputAt, onAddOutputAt, onDuplicateNode, onDeleteEdge, onUpdateNode, onDeleteNode,
  onNodeClick, onInit, onFitView, onAutoLayout, isValidConnection,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{
    kind: 'pane' | 'node' | 'edge';
    x: number;
    y: number;
    position?: { x: number; y: number };
    id?: string;
  } | null>(null);
  const quickFilters = ['scale', 'crop', 'deinterlace', 'hqdn3d', 'loudnorm', 'aresample', 'volume']
    .map((id) => FILTER_PRESETS.find((preset) => preset.id === id))
    .filter((preset): preset is FilterPreset => !!preset);

  useLayoutEffect(() => {
    if (!contextMenu || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const x = Math.max(8, Math.min(contextMenu.x, window.innerWidth - rect.width - 8));
    const y = Math.max(8, Math.min(contextMenu.y, window.innerHeight - rect.height - 8));
    if (x !== contextMenu.x || y !== contextMenu.y) setContextMenu({ ...contextMenu, x, y });
  }, [contextMenu]);

  useEffect(() => {
    if (!contextMenu) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContextMenu(null);
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []);
      if (!buttons.length) return;
      event.preventDefault();
      const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
        : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[index].focus();
    };
    window.addEventListener('keydown', closeOnEscape);
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [contextMenu?.kind, contextMenu?.id]);

  const handlePaneContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setContextMenu({
      kind: 'pane', x: event.clientX, y: event.clientY,
      position: { x: event.clientX - bounds.left, y: event.clientY - bounds.top },
    });
  };

  const activeNode = contextMenu?.kind === 'node' ? workflow.nodes.find((node) => node.id === contextMenu.id) : undefined;

  return (
    <div className="advanced-workspace">
      <FilterLibrary mode="advanced" onAdd={onAddPreset} onDragStart={onPresetDragStart} />
      <section className="canvas-region" aria-label="高级节点画布">
        <div className="canvas-toolbar">
          <div className="canvas-toolbar__title"><span className="canvas-live-dot" /><span>{workflow.nodes.length} 个节点 · {workflow.edges.length} 条连接</span></div>
          <div className="canvas-toolbar__actions">
            <button type="button" className="canvas-tool" onClick={onAddInput}><FilePlus2 size={15} /><span>添加输入节点</span></button>
            <button type="button" className="canvas-tool" onClick={onAddOutput}><FileOutput size={15} /><span>添加输出节点</span></button>
            <button type="button" className="canvas-tool" onClick={onAutoLayout}><AlignHorizontalSpaceAround size={15} /><span>整理布局</span></button>
            <button type="button" className="icon-button" onClick={onFitView} title="适配画布" aria-label="适配画布"><Maximize2 size={16} /></button>
          </div>
        </div>
        <div className="flow-canvas" ref={canvasRef} onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={workflow.nodes}
            edges={workflow.edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(event, node) => { setContextMenu(null); onNodeClick(event, node as WorkflowNode); }}
            onNodeContextMenu={(event, node) => {
              event.preventDefault();
              event.stopPropagation();
              onNodeClick(event, node as WorkflowNode);
              setContextMenu({ kind: 'node', id: node.id, x: event.clientX, y: event.clientY });
            }}
            onEdgeContextMenu={(event, edge) => {
              event.preventDefault();
              event.stopPropagation();
              setContextMenu({ kind: 'edge', id: edge.id, x: event.clientX, y: event.clientY });
            }}
            onPaneContextMenu={handlePaneContextMenu}
            onPaneClick={() => setContextMenu(null)}
            onInit={onInit}
            isValidConnection={isValidConnection}
            fitView
            fitViewOptions={{ padding: 0.22, maxZoom: 1.15 }}
            minZoom={0.25}
            maxZoom={1.5}
            deleteKeyCode={['Backspace', 'Delete']}
            selectionKeyCode="Shift"
            defaultEdgeOptions={{ type: 'smoothstep', animated: false }}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1.15} color="var(--color-rule)" />
            <MiniMap className="flow-minimap" pannable zoomable nodeStrokeWidth={3} />
            <Controls showInteractive={false} position="bottom-right" />
          </ReactFlow>
        </div>
      </section>
      <NodeInspector node={selectedNode} onChange={onUpdateNode} onDelete={onDeleteNode} />
      {contextMenu && (
        <div className="context-dismiss" onMouseDown={() => setContextMenu(null)} onContextMenu={(event) => { event.preventDefault(); setContextMenu(null); }}>
          <div
            ref={menuRef}
            className="canvas-context-menu"
            role="menu"
            aria-label={contextMenu.kind === 'pane' ? '画布操作' : contextMenu.kind === 'node' ? '节点操作' : '连线操作'}
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onMouseDown={(event) => event.stopPropagation()}
            onContextMenu={(event) => { event.preventDefault(); event.stopPropagation(); }}
          >
            {contextMenu.kind === 'pane' && contextMenu.position && <>
              <p className="context-menu-heading">添加到此处</p>
              <button type="button" role="menuitem" onClick={() => { onAddInputAt(contextMenu.position as { x: number; y: number }); setContextMenu(null); }}><FilePlus2 size={15} />输入文件节点</button>
              <button type="button" role="menuitem" onClick={() => { onAddOutputAt(contextMenu.position as { x: number; y: number }); setContextMenu(null); }}><FileOutput size={15} />输出文件节点</button>
              <p className="context-menu-heading">常用滤镜</p>
              {quickFilters.map((preset) => <button type="button" role="menuitem" key={preset.id} onClick={() => { onAddPresetAt(preset, contextMenu.position as { x: number; y: number }); setContextMenu(null); }}><Plus size={14} />{preset.label}<span className={'context-menu-stream context-menu-stream--' + preset.streamType}>{preset.streamType === 'audio' ? '音频' : '视频'}</span></button>)}
              <div className="context-menu-divider" />
              <button type="button" role="menuitem" onClick={() => { onAutoLayout(); setContextMenu(null); }}><AlignHorizontalSpaceAround size={15} />整理布局</button>
              <button type="button" role="menuitem" onClick={() => { onFitView(); setContextMenu(null); }}><Maximize2 size={15} />适配画布</button>
            </>}
            {contextMenu.kind === 'node' && activeNode && <>
              <p className="context-menu-heading">{activeNode.data.label}</p>
              {activeNode.type !== 'output' && <button type="button" role="menuitem" onClick={() => { onDuplicateNode(activeNode.id); setContextMenu(null); }}><Copy size={15} />复制节点与连线</button>}
              <button type="button" role="menuitem" className="context-menu-danger" onClick={() => { onDeleteNode(activeNode.id); setContextMenu(null); }}><Trash2 size={15} />删除节点</button>
            </>}
            {contextMenu.kind === 'edge' && <>
              <p className="context-menu-heading">连线操作</p>
              <button type="button" role="menuitem" className="context-menu-danger" onClick={() => { if (contextMenu.id) onDeleteEdge(contextMenu.id); setContextMenu(null); }}><Trash2 size={15} />删除此连线</button>
            </>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedWorkspace;
